import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import type { PublicUserProfile } from "@xop/dashboard-shared";

import { api } from "../api.js";

/**
 * Fetch a peer's public profile (`{ id, name, email, dashboard_role }`) for the
 * click-to-view profile card. Backed by `GET /api/users/:id`, which reads the
 * dashboard's own user store — so this is cheap and shared across every card
 * for the same person. Disabled for a non-positive id (bot/system senders,
 * synthetic rows); a `404` (unknown user) is NOT retried — the card just
 * degrades to the name it was handed. Identity rarely changes within a session,
 * so results stay fresh for a few minutes.
 */
export function useUserProfile(userId: number | null): UseQueryResult<PublicUserProfile> {
  return useQuery({
    queryKey: ["user-profile", userId] as const,
    enabled: typeof userId === "number" && Number.isInteger(userId) && userId > 0,
    queryFn: () => api<PublicUserProfile>(`/api/users/${userId}`),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}
